import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, EyeOff, X, Newspaper } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";
const emptyForm = { title: "", excerpt: "", content: "", cover_image_url: "", published: true };

export default function BlogView() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, "new" = create, id = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/blog");
      setPosts(data);
    } catch (e) { toast.error("Ratgeber-Beiträge konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (p) => { setForm({ title: p.title, excerpt: p.excerpt, content: p.content, cover_image_url: p.cover_image_url, published: p.published }); setEditing(p.id); };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) { toast.error("Titel und Inhalt sind erforderlich."); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/blog", form);
        setPosts((p) => [data, ...p]);
        toast.success("Beitrag veröffentlicht.");
      } else {
        const { data } = await api.patch(`/blog/${editing}`, form);
        setPosts((p) => p.map((x) => (x.id === editing ? data : x)));
        toast.success("Beitrag gespeichert.");
      }
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const togglePublished = async (p) => {
    try {
      const { data } = await api.patch(`/blog/${p.id}`, { published: !p.published });
      setPosts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/blog/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Beitrag gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  return (
    <div data-testid="blog-view">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-2">Ratgeber</h1>
          <p className="text-sm text-[#888] max-w-xl">Artikel, die auf /ratgeber öffentlich sichtbar sind, sobald sie veröffentlicht sind.</p>
        </div>
        <button onClick={openNew} data-testid="blog-new-btn" className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors">
          <Plus size={16} /> Neuer Beitrag
        </button>
      </div>

      {editing && (
        <form onSubmit={save} data-testid="blog-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-8 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl text-white">{editing === "new" ? "Neuer Beitrag" : "Beitrag bearbeiten"}</h3>
            <button type="button" onClick={close} data-testid="blog-form-close" className="text-[#888] hover:text-white"><X size={18} /></button>
          </div>
          <input data-testid="blog-title-input" value={form.title} onChange={set("title")} placeholder="Titel" className={inputCls} />
          <input data-testid="blog-excerpt-input" value={form.excerpt} onChange={set("excerpt")} placeholder="Kurzbeschreibung (für die Übersicht)" className={inputCls} />
          <input data-testid="blog-cover-input" value={form.cover_image_url} onChange={set("cover_image_url")} placeholder="Bild-URL (optional)" className={inputCls} />
          <textarea data-testid="blog-content-input" value={form.content} onChange={set("content")} placeholder="Inhalt (Absätze durch Leerzeile trennen)" rows={10} className={`${inputCls} resize-y`} />
          <label className="flex items-center gap-2 text-sm text-[#ccc]">
            <input type="checkbox" data-testid="blog-published-checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
            Veröffentlicht (öffentlich sichtbar)
          </label>
          <button type="submit" disabled={saving} data-testid="blog-save-btn" className="rounded-full bg-white text-[#050505] px-6 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : posts.length === 0 ? (
        <div data-testid="blog-empty-state" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <Newspaper size={28} className="text-[#555] mx-auto mb-3" />
          <p className="text-[#888]">Noch keine Beiträge angelegt.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="blog-list">
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-post-${p.id}`} className={`rounded-2xl border p-5 flex items-center justify-between gap-4 flex-wrap ${p.published ? "border-white/10 bg-[#0a0a0a]" : "border-white/5 bg-[#0a0a0a]/50 opacity-60"}`}>
              <div>
                <p className="text-white font-medium">{p.title}</p>
                <p className="text-xs text-[#666] font-mono mt-1">/ratgeber/{p.slug}</p>
              </div>
              <div className="flex items-center gap-2">
                <span data-testid={`blog-status-${p.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${p.published ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-white/5 text-[#888] border border-white/10"}`}>
                  {p.published ? "Veröffentlicht" : "Entwurf"}
                </span>
                <button onClick={() => togglePublished(p)} data-testid={`blog-toggle-${p.id}`} title={p.published ? "Verbergen" : "Veröffentlichen"} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white transition-colors">
                  {p.published ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white transition-colors">
                  <Pencil size={16} />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
